import type { BookingDetail } from '@massilia/queries'

import { Button, Separator } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import { CreditCard } from 'lucide-react'
import { useMemo } from 'react'

import { SummaryRow } from '@/components/payment-confirmation/summary-row'
import { BankDetailsGrid } from '@/components/shared/bank-details-grid'
import { centsToEuros, formatPrice } from '@/utils/format-price'

interface PaymentSettlementSectionProps {
  booking: BookingDetail
}

export function PaymentSettlementSection({ booking }: PaymentSettlementSectionProps) {
  const isAwaitingDeposit = booking.status === 'AWAITING_DEPOSIT'
  const isReunion = booking.flightSegments[0]?.departureLocation === 'RUN'

  const paidAmount = useMemo(
    () => booking.payments.filter((p) => p.status === 'PAID').reduce((sum, p) => sum + p.amount, 0),
    [booking.payments]
  )

  const remainingAmount = booking.totalPrice - paidAmount
  const deadlineLabel = formatDeadline(booking.depositDeadline)

  const deadlineRowLabel = isAwaitingDeposit
    ? 'Date limite de réception du virement'
    : 'Date limite de paiement du solde'

  return (
    <div className="border-gray-light-200 flex flex-col gap-3 rounded-2xl border bg-white p-6">
      <div className="text-gray-light-900 text-lg font-semibold sm:text-2xl">
        Règlement de votre réservation et modalités de paiements
      </div>

      {deadlineLabel && <SummaryRow label={deadlineRowLabel} value={deadlineLabel} />}
      <SummaryRow label="Acomptes versés" value={formatPrice(centsToEuros(paidAmount))} />
      <SummaryRow label="Reste à payer" value={formatPrice(centsToEuros(remainingAmount))} />

      <Separator />

      {isAwaitingDeposit && <BankDetailsGrid isReunion={isReunion} />}

      <div className="mt-1">
        <Button asChild className="bg-brand-600 hover:bg-brand-700">
          <Link
            search={{
              email: booking.client.email ?? '',
              lastName: booking.passengers[0]?.lastName ?? '',
              dossierNumber: booking.reference,
            }}
            to="/payer-acompte"
          >
            <CreditCard className="mr-2 size-4" />
            Payer un acompte en CB
          </Link>
        </Button>
      </div>
    </div>
  )
}

function formatDeadline(isoDate: null | string | undefined): string | undefined {
  if (!isoDate) return undefined
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(isoDate))
  } catch {
    return isoDate
  }
}
